import { Link } from "react-router-dom";
import { ArrowRight, Ship, PhoneCall, FileCheck2, Container } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { ProductCard } from "@/components/site/ProductCard";
import { ScamWarning } from "@/components/site/ScamWarning";
import { useProducts } from "@/lib/useProducts";

const STEPS = [
  { icon: FileCheck2, t: "Send a quote request", d: "Product, quantity, destination port and Incoterm. Takes two minutes." },
  { icon: PhoneCall, t: "We call you back", d: "We confirm grade, packing and shipping window by phone or WhatsApp." },
  { icon: Container, t: "Documents on request", d: "Phytosanitary certificates and lab reports shared by secure link." },
  { icon: Ship, t: "Pack and ship", d: "Packed to your carton spec and loaded at JNPT or Mundra." },
];

const Hero = () => (
  <section className="relative min-h-[80vh] flex flex-col justify-center bg-stone-900 text-white overflow-hidden grain" data-testid="home-hero">
    <img src="/img/hero.jpg" alt="Container ship loaded at port" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-950/55 to-stone-950/20" />
    <div className="relative max-w-7xl mx-auto w-full px-6 md:px-12 py-24">
      <p className="fade-up text-xs uppercase tracking-[0.25em] text-emerald-300 mb-6">Fresh produce exporter · India</p>
      <h1 className="fade-up text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl leading-[1.05]" style={{ animationDelay: "80ms" }}>
        Bananas, onions and pomegranates, packed by the container.
      </h1>
      <p className="fade-up mt-6 max-w-xl text-base md:text-lg text-stone-200" style={{ animationDelay: "160ms" }}>
        G9 bananas, Nashik red onions and Bhagwa pomegranates for importers and distributors in the UAE, Saudi Arabia and Europe. FOB or CIF.
      </p>
      <div className="fade-up mt-10 flex flex-wrap gap-4" style={{ animationDelay: "240ms" }}>
        <Link to="/request-quote" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 rounded-full bg-amber-700 hover:bg-amber-800 px-7 py-3.5 font-medium transition-colors">
          Request a quote <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/products" data-testid="hero-products-btn" className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 backdrop-blur-md hover:bg-white/20 px-7 py-3.5 font-medium transition-colors">
          See products and seasons
        </Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  const { data: products = [] } = useProducts();
  return (
    <>
      <Seo description="SLN Agro Exports supplies G9 banana, red onion and Bhagwa pomegranate from India to importers in the UAE, Saudi Arabia and the EU. FOB and CIF, by the container." path="/" />
      <Hero />
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
          <h2 className="md:col-span-7 text-3xl lg:text-4xl font-semibold tracking-tight">Three products. Each shipped in its own season.</h2>
          <p className="md:col-span-5 text-stone-600">Every product page shows specs, carton options, MOQ and a month-by-month season calendar, so you know what you can order now.</p>
        </div>
        <div className="grid md:grid-cols-12 gap-6" data-testid="home-products-grid">
          {products.map((p, i) => (
            <div key={p.slug} className={i === 0 ? "md:col-span-7 md:row-span-2" : "md:col-span-5"}>
              <ProductCard product={p} large={i === 0} />
            </div>
          ))}
        </div>
      </section>
      <section className="bg-emerald-950 text-emerald-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight">How an order works</h2>
            <p className="mt-4 text-emerald-200/80">No online checkout. Container orders are confirmed by phone and paid by LC or TT.</p>
          </div>
          <ol className="md:col-span-8 grid sm:grid-cols-2 gap-px bg-emerald-800/60 rounded-lg overflow-hidden">
            {STEPS.map((s, i) => (
              <li key={s.t} className="bg-emerald-950 p-8 space-y-3" data-testid={`home-step-${i + 1}`}>
                <s.icon className="h-6 w-6 text-amber-500" />
                <p className="text-xs text-emerald-400">0{i + 1}</p>
                <h3 className="text-xl font-medium">{s.t}</h3>
                <p className="text-sm text-emerald-200/80">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <ScamWarning />
      </section>
    </>
  );
}
